import { useEffect, useMemo, useRef, useState } from "react"
import { DimensionDisplayType, useFilterQueryParams } from "../../../hooks/filter_query_params"
import { ZoneType } from "@ciq/shared/utils/const"
import {
  ALWAYS_SHOW_FILTER,
  BaseLayer,
  getFirstSymbolLayerId,
  SKIP_RESOURCE_TYPES,
} from "./base_layer"
import { useMapData } from "../useMapData"
import { DEFAULT_METRIC_FILL_COLOR, DEFAULT_METRIC_OUTLINE_COLOR } from "../../../utils/colors"
import { isEmptyObject } from "@ciq/shared/utils/object"

export const FlatMapKeys = {
  SOURCE_ID: "da-composite-flat-data",
  FAN_SOURCE_ID: "da-composite-fan-data",

  Layers: {
    BUILDING: "da-building-layer",
    FLOOR: "da-floor-layer",
    BOUNDING_BOX: "da-floor-fan-bbox-layer",
    FLOOR_FAN: "da-floor-fan-layer",
    DEFAULT_FAN: "da-default-fan-layer",
    DEFAULT: "da-default-layer",
    BASE_OUTLINE: "da-base-outline-layer",
    FAN_OUTLINE: "da-fan-outline-layer",
  },
}

export const INTERACTABLE_MAP_FLAT_LAYER_IDS = Object.values(FlatMapKeys.Layers).map(
  layerId => `${layerId}-flat`
)

export const FanBboxLayerId = `${FlatMapKeys.Layers.BOUNDING_BOX}-flat`

// Opacity for selected resources
export const SELECTED_OPACITY = 0.8

// Opacity for non-selected resources
export const FADED_OPACITY = 0.2

// Static filters applied during layer creation
const DEFAULT_FILTERS = {
  [FlatMapKeys.Layers.BUILDING]: ["==", "subType", ZoneType.BUILDING],
}

/**
 * Class to manage a pair of layers: base (white background) + colored layer
 */
class LayerPair {
  constructor(sourceId, layerId, filter = null) {
    this.layerId = layerId

    // Create base layer (white background)
    this.baseLayer = new BaseLayer(
      `${layerId}-base`,
      "fill",
      sourceId,
      {
        "fill-color": "#FFF",
        "fill-opacity": 1,
      },
      filter
    )

    // Create color layer
    this.colorLayer = new BaseLayer(
      `${layerId}-flat`,
      "fill",
      sourceId,
      {
        "fill-color": ["coalesce", ["feature-state", "color"], DEFAULT_METRIC_FILL_COLOR],
        "fill-outline-color": DEFAULT_METRIC_OUTLINE_COLOR,
        "fill-opacity": FADED_OPACITY,
      },
      filter
    )
  }

  /**
   * Add both base and color layers to the map
   */
  addToMap(map, insertAboveLayerId, filter = null) {
    this.baseLayer.addToMap(map, insertAboveLayerId, filter)
    this.colorLayer.addToMap(map, insertAboveLayerId, filter)
  }

  /**
   * Update visibility for both layers
   */
  setVisibility(map, visible) {
    this.baseLayer.setVisibility(map, visible)
    this.colorLayer.setVisibility(map, visible)
  }

  /**
   * Update filter for both layers
   */
  setFilter(map, filter) {
    this.baseLayer.setFilter(map, filter)
    this.colorLayer.setFilter(map, filter)
  }

  /**
   * Update paint property for the colored layer
   */
  setPaintProperty(map, property, value) {
    this.colorLayer.setPaintProperty(map, property, value)
  }
}

export const FAN_OUTLINE_DEFAULT_WIDTH = 1.5

/**
 * Class to manage the outline layer for selected zones
 */
class OutlineLayer extends BaseLayer {
  constructor(sourceId, layerId) {
    super(layerId, "line", sourceId, {
      "line-width": FAN_OUTLINE_DEFAULT_WIDTH,
      "line-color": "#F85755",
    })
  }
}

/**
 * Class to manage the bounding box for the fanned zones
 */
class FanBoundingBoxLayer extends BaseLayer {
  constructor() {
    super(
      FanBboxLayerId,
      "fill",
      FlatMapKeys.FAN_SOURCE_ID,
      {
        "fill-color": "#FFF",
        "fill-outline-color": DEFAULT_METRIC_FILL_COLOR,
        "fill-opacity": 1,
      },
      ALWAYS_SHOW_FILTER
    )
  }
}

/**
 * Compute the hierarchy of selected resources including selected IDs, fan IDs, and outline IDs.
 *
 * @param {Object|null} selectedResource - The currently selected resource
 * @param {Function} getParent - Function to get the parent of a resource
 * @param {Function} subZoneIter - Function to iterate over sub-zones of a resource
 * @param {Function} getFloorZones - Function to get all floor zones
 * @param {Array<string>} skipResourceTypes - Resource types to skip when building hierarchy
 * @returns {{selectedResourceIds: Array<string>, fanSelectedResourceIds: Array<string>, outlineResourceIds: Array<string>}} The computed resource IDs
 */
export function computeSelectedResourceHierarchy(
  selectedResource,
  getParent,
  subZoneIter,
  getFloorZones,
  skipResourceTypes
) {
  if (!selectedResource?.id) {
    return {
      selectedResourceIds: [],
      fanSelectedResourceIds: [],
      outlineResourceIds: [],
    }
  }

  const newSelectedResourceIds = new Set()
  const newFanSelectedResourceIds = new Set()
  const newOutlineResourceIds = new Set()

  if (selectedResource?.id) {
    newFanSelectedResourceIds.add(selectedResource.id)

    let current = selectedResource
    while (current != null) {
      newOutlineResourceIds.add(current.id)
      current = getParent(current)
    }
  }

  const add = resource => {
    if (skipResourceTypes.includes(resource.type)) {
      return
    }

    newSelectedResourceIds.add(resource.id)
  }

  let current = selectedResource
  while (current) {
    if (current.type === ZoneType.FLOOR) {
      for (const child of subZoneIter(current)) {
        add(child)
      }
    }

    add(current)
    current = getParent(current)
  }

  if (selectedResource?.type === ZoneType.BUILDING) {
    for (const child of getFloorZones().filter(zone => zone.parentId === selectedResource.id)) {
      newFanSelectedResourceIds.add(child.id)
      // Include end zones within building floors for End Zone View opacity
      for (const subChild of subZoneIter(child)) {
        add(subChild)
      }
    }
  }

  return {
    selectedResourceIds: Array.from(newSelectedResourceIds),
    fanSelectedResourceIds: Array.from(newFanSelectedResourceIds),
    outlineResourceIds: Array.from(newOutlineResourceIds),
  }
}

/**
 * Build a Mapbox opacity expression based on selected resource IDs.
 *
 * Zone IDs (selected, fan, outline) match against feature `id` (zone UUID).
 *
 * @param {Array<string>} selectedResourceIds - IDs of selected resources
 * @param {Array<string>} fanSelectedResourceIds - IDs of fan-selected resources
 * @param {Array<string>} outlineResourceIds - IDs of outlined resources
 * @returns {number|Array} Opacity value (1) or Mapbox expression array for conditional opacity
 */
export function buildOpacityExpression(
  selectedResourceIds,
  fanSelectedResourceIds,
  outlineResourceIds
) {
  // If no resources are selected, use full opacity
  if (
    selectedResourceIds.length === 0 &&
    fanSelectedResourceIds.length === 0 &&
    outlineResourceIds.length === 0
  ) {
    return SELECTED_OPACITY
  }

  // Zone IDs match against feature "id" (zone UUID)
  const zoneIds = [
    ...selectedResourceIds,
    ...fanSelectedResourceIds,
    ...outlineResourceIds,
  ]

  // Full opacity for matched features, faded for others
  return [
    "case",
    ["in", ["get", "id"], ["literal", zoneIds]],
    ["literal", SELECTED_OPACITY],
    ["*", FADED_OPACITY],
  ]
}

/**
 * Build a Mapbox filter for default zones based on zone type.
 *
 * @param {string} zoneType - The current zone type filter
 * @returns {Array} Mapbox filter expression
 */
export function buildDefaultFilter(zoneType) {
  const filters = ["all", ["==", "subType", ZoneType.DEFAULT]]

  if (zoneType === ZoneType.BUILDING) {
    filters.push(["==", "viewType", ""])
  }

  return filters
}

/**
 * Build a Mapbox filter for the base outline layer.
 *
 * @param {string|null} selectedResourceId - The ID of the selected resource
 * @param {string} zoneType - The current zone type filter
 * @returns {Array} Mapbox filter expression
 */
export function buildBaseOutlineFilter(selectedResourceId, zoneType) {
  const filters = ["all", ["==", "id", selectedResourceId ?? ""]]

  if (zoneType === ZoneType.BUILDING) {
    filters.push(["==", "subType", ZoneType.BUILDING])
  }

  return filters
}

/**
 * Build a Mapbox filter for the fan outline layer.
 *
 * @param {string|null} selectedResourceId - The ID of the selected resource
 * @returns {Array} Mapbox filter expression
 */
export function buildFanOutlineFilter(selectedResourceId) {
  return ["==", "id", selectedResourceId ?? ""]
}

export function FlatMapSource({ map, isMapLoading }) {
  const { selectedResource, geojson, mapColors, getParent, subZoneIter, getFloorZones } =
    useMapData()
  const { zoneType, dimensionDisplayType, isHidingOccupancy, campusFlow } = useFilterQueryParams()

  const [isLoaded, setIsLoaded] = useState(false)

  // Initialize individual layer managers
  const buildingLayerRef = useRef(new LayerPair(FlatMapKeys.SOURCE_ID, FlatMapKeys.Layers.BUILDING))
  const floorLayerRef = useRef(new LayerPair(FlatMapKeys.SOURCE_ID, FlatMapKeys.Layers.FLOOR))
  const defaultLayerRef = useRef(new LayerPair(FlatMapKeys.SOURCE_ID, FlatMapKeys.Layers.DEFAULT))
  const floorFanLayerRef = useRef(
    new LayerPair(FlatMapKeys.FAN_SOURCE_ID, FlatMapKeys.Layers.FLOOR_FAN, [
      "==",
      "subType",
      ZoneType.FLOOR,
    ])
  )
  const defaultFanLayerRef = useRef(
    new LayerPair(FlatMapKeys.FAN_SOURCE_ID, FlatMapKeys.Layers.DEFAULT_FAN, [
      "==",
      "subType",
      ZoneType.DEFAULT,
    ])
  )

  // Keep array structure for easy iteration
  const layerPairs = useMemo(
    () => [
      buildingLayerRef.current,
      floorLayerRef.current,
      defaultLayerRef.current,
      floorFanLayerRef.current,
      defaultFanLayerRef.current,
    ],
    []
  )

  const baseOutlineLayerRef = useRef(
    new OutlineLayer(FlatMapKeys.SOURCE_ID, FlatMapKeys.Layers.BASE_OUTLINE)
  )
  const fanOutlineLayerRef = useRef(
    new OutlineLayer(FlatMapKeys.FAN_SOURCE_ID, FlatMapKeys.Layers.FAN_OUTLINE)
  )
  const boundingBoxLayerRef = useRef(new FanBoundingBoxLayer())

  // Compute selected resource hierarchy
  const { selectedResourceIds, fanSelectedResourceIds, outlineResourceIds } = useMemo(
    () =>
      computeSelectedResourceHierarchy(
        selectedResource,
        getParent,
        subZoneIter,
        getFloorZones,
        SKIP_RESOURCE_TYPES
      ),
    [getFloorZones, getParent, selectedResource, subZoneIter]
  )

  // Initialize map sources and layers
  useEffect(() => {
    if (!map || isMapLoading || isLoaded) {
      return
    }

    const firstSymbolId = getFirstSymbolLayerId(map)

    // Add base source if not exists
    if (!map.getSource(FlatMapKeys.SOURCE_ID)) {
      map.addSource(FlatMapKeys.SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        promoteId: "id",
      })
    }

    // Add fan source if not exists
    if (!map.getSource(FlatMapKeys.FAN_SOURCE_ID)) {
      map.addSource(FlatMapKeys.FAN_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        promoteId: "id",
      })
    }

    // Add all layer pairs
    for (const layerPair of layerPairs) {
      const symbol = layerPair.layerId === FlatMapKeys.Layers.BUILDING ? firstSymbolId : null

      if (layerPair.layerId === FlatMapKeys.Layers.FLOOR_FAN) {
        // Add bounding box layer before floor fan
        boundingBoxLayerRef.current.addToMap(map, symbol)
      }

      const defaultFilter = DEFAULT_FILTERS[layerPair.layerId]
      layerPair.addToMap(map, symbol, defaultFilter)
    }

    // Add outline layers
    baseOutlineLayerRef.current.addToMap(map, null)
    fanOutlineLayerRef.current.addToMap(map, null)

    setIsLoaded(true)
  }, [isLoaded, isMapLoading, layerPairs, map])

  // Update base geojson data
  useEffect(() => {
    if (!map || isMapLoading || !map.getSource(FlatMapKeys.SOURCE_ID) || !geojson?.flat) {
      return
    }

    map.getSource(FlatMapKeys.SOURCE_ID).setData(geojson?.flat)
  }, [geojson?.flat, map, isMapLoading])

  // Update fan geojson data
  useEffect(() => {
    if (!map || isMapLoading || !map.getSource(FlatMapKeys.FAN_SOURCE_ID) || !geojson?.fan) {
      return
    }

    map.getSource(FlatMapKeys.FAN_SOURCE_ID).setData(geojson?.fan)
  }, [geojson?.fan, map, isMapLoading])

  // Update layer opacity based on selection
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const opacityExpression = buildOpacityExpression(
      selectedResourceIds,
      fanSelectedResourceIds,
      outlineResourceIds
    )

    // Update opacity only for non-fan layers
    buildingLayerRef.current.setPaintProperty(map, "fill-opacity", opacityExpression)
    floorLayerRef.current.setPaintProperty(map, "fill-opacity", opacityExpression)
    defaultLayerRef.current.setPaintProperty(map, "fill-opacity", opacityExpression)
  }, [map, isLoaded, selectedResourceIds, fanSelectedResourceIds, outlineResourceIds])

  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    // Set fan layer opacity to full
    floorFanLayerRef.current.setPaintProperty(map, "fill-opacity", SELECTED_OPACITY)
    defaultFanLayerRef.current.setPaintProperty(map, "fill-opacity", SELECTED_OPACITY)
    boundingBoxLayerRef.current.setPaintProperty(map, "fill-opacity", 1)
  }, [map, isLoaded])

  // Update layer visibility
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const layerVisible = dimensionDisplayType === DimensionDisplayType.TWO_D

    buildingLayerRef.current.setVisibility(map, layerVisible)
    floorLayerRef.current.setVisibility(map, layerVisible)
    defaultLayerRef.current.setVisibility(map, layerVisible)
    baseOutlineLayerRef.current.setVisibility(map, layerVisible)
  }, [dimensionDisplayType, isLoaded, map])

  // Update fan layer visibility
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const fanLayersVisible =
      dimensionDisplayType === DimensionDisplayType.TWO_D && zoneType === ZoneType.BUILDING && !campusFlow

    floorFanLayerRef.current.setVisibility(map, fanLayersVisible)
    defaultFanLayerRef.current.setVisibility(map, fanLayersVisible)
    fanOutlineLayerRef.current.setVisibility(map, fanLayersVisible)
    boundingBoxLayerRef.current.setVisibility(map, fanLayersVisible)
  }, [campusFlow, dimensionDisplayType, isLoaded, map, selectedResource?.type, zoneType])

  // Update default layer filter
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const filter = buildDefaultFilter(zoneType)
    defaultLayerRef.current.setFilter(map, filter)
  }, [isLoaded, map, zoneType])

  // Update building layer filter
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const filter = DEFAULT_FILTERS[FlatMapKeys.Layers.BUILDING]
    buildingLayerRef.current.setFilter(map, filter)
  }, [isLoaded, map])

  // Update base outline filter
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const outlineFilter = buildBaseOutlineFilter(selectedResource?.id, zoneType)
    baseOutlineLayerRef.current.setFilter(map, outlineFilter)
  }, [isLoaded, map, selectedResource?.id, zoneType])

  // Update fan outline filter
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    const outlineFilter = buildFanOutlineFilter(selectedResource?.id)
    fanOutlineLayerRef.current.setFilter(map, outlineFilter)
  }, [isLoaded, map, selectedResource?.id])

  // Update feature states when colors change or occupancy toggle changes
  useEffect(() => {
    if (!map || !isLoaded) {
      return
    }

    if (isHidingOccupancy || isEmptyObject(mapColors)) {
      try {
        map.removeFeatureState({ source: FlatMapKeys.SOURCE_ID })
      } catch (e) {
        console.warn("FlatMapSource: Failed to remove feature state from base source", e)
      }
      try {
        map.removeFeatureState({ source: FlatMapKeys.FAN_SOURCE_ID })
      } catch (e) {
        console.warn("FlatMapSource: Failed to remove feature state from fan source", e)
      }
      return
    }

    for (const [zoneId, color] of Object.entries(mapColors)) {
      try {
        map.setFeatureState({ source: FlatMapKeys.SOURCE_ID, id: zoneId }, { color })
        map.setFeatureState({ source: FlatMapKeys.FAN_SOURCE_ID, id: zoneId }, { color })
      } catch (e) {
        console.warn(`FlatMapSource: Failed to set feature state for zone ${zoneId}`, e)
      }
    }
  }, [map, isLoaded, isHidingOccupancy, mapColors])

  return null
}
